/**
 * Botão "Enviar / Reenviar questionário" na seção de ações do detalhe
 * do lead. Liga a plumbing que já existe (`useSendOnboardingLink` +
 * `useDefaultOnboardingTemplate`) — era placeholder disabled do M1-01b
 * e agora vira acionável.
 *
 * **Por que em arquivo separado**: mesma lógica de `LeadFormSubmissionsSection`
 * (PR #115). LeadDetailPage tem dívida pré-existente de tamanho;
 * extraímos ações operacionais para componentes focados em vez de
 * engordar o pai.
 *
 * **Estados visuais** (todos via tokens do DS):
 *   - Template carregando       → botão desabilitado, label "Carregando…"
 *   - Template não publicado    → desabilitado + `title` explicando
 *     como publicar (hint visível também no parágrafo da seção-pai)
 *   - Lead sem email            → desabilitado com aviso — email é
 *     obrigatório no create, mas defensivo contra deleção manual
 *   - Pronto                    → habilitado; label muda conforme
 *     stage ("Enviar" se ainda `new`, "Reenviar" caso contrário)
 *   - Enviando                  → desabilitado + "Enviando…"
 *
 * **Invalidação de cache**: após sucesso, além do que o hook já
 * invalida (lead detail, activities, lista), também invalidamos
 * `FORMS_KEYS.leadSubmissions(leadId)` para a seção "Respostas do
 * questionário" refrescar imediatamente — a nova submission em
 * `status=pending` aparece com aviso de "rascunho em aberto" até
 * o lead responder.
 */
import { useQueryClient } from '@tanstack/react-query';
import { Button, toast } from '@kmgestao/ui';

import { FORMS_KEYS } from '../../../features/forms/queries.js';
import { useSendOnboardingLink, type Lead, type LeadStage } from '../../../features/leads/index.js';
import { useDefaultOnboardingTemplate } from '../../../features/onboarding/index.js';

export interface SendQuestionnaireButtonProps {
  lead: Lead;
}

/** Estágios em que "Enviar questionário" é a ação natural (nunca foi
 * enviado). Em todos os outros estágios o label vira "Reenviar". */
const NEVER_SENT_STAGES: readonly LeadStage[] = ['new'];

export function SendQuestionnaireButton({ lead }: SendQuestionnaireButtonProps) {
  const template = useDefaultOnboardingTemplate();
  const sendOnboardingLink = useSendOnboardingLink();
  const qc = useQueryClient();

  const templateReady =
    !template.isLoading && template.data !== null && template.data !== undefined;
  const hasEmail = Boolean(lead.email?.trim().length);
  const isNew = NEVER_SENT_STAGES.includes(lead.stage);
  const label = isNew ? 'Enviar questionário' : 'Reenviar questionário';

  const disabledReason = (() => {
    if (template.isLoading) return 'Carregando template padrão…';
    if (!templateReady) return 'Publique um template em Configurações → Onboarding.';
    if (!hasEmail) return 'Lead sem email cadastrado — edite os dados antes de enviar.';
    return null;
  })();

  const onClick = async () => {
    if (!template.data) return;
    try {
      const result = await sendOnboardingLink.mutateAsync({
        lead_id: lead.id,
        template_id: template.data.id,
      });
      qc.invalidateQueries({ queryKey: FORMS_KEYS.leadSubmissions(lead.id) });

      if (result.email_status === 'sent') {
        toast.success(`Questionário enviado para ${lead.email}.`);
      } else if (result.email_status === 'skipped') {
        toast.info('Questionário preparado, mas o email não foi enviado (modo dev).');
      } else {
        toast.warning(
          'Questionário preparado, mas o email falhou. Tente novamente em alguns minutos.',
        );
      }
    } catch (err) {
      toast.error('Falha ao enviar questionário', {
        description: (err as Error).message,
      });
    }
  };

  const busy = sendOnboardingLink.isPending;
  const disabled = busy || disabledReason !== null;

  return (
    <Button
      size="lg"
      onClick={onClick}
      disabled={disabled}
      title={disabledReason ?? undefined}
      aria-busy={busy || undefined}
    >
      {busy ? 'Enviando…' : template.isLoading ? 'Carregando…' : label}
    </Button>
  );
}
